import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import {
  IOutfall,
  IParameterLimitRow,
  ComplianceStatus,
  TrendDirection,
} from '../../../types/discharge';

interface IOutfallDetailPanelProps {
  outfall: IOutfall;
  onClose: () => void;
}

// ─── animations ───────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

// ─── layout ───────────────────────────────────────────────────────────────────

const Panel = styled.aside`
  position: absolute;
  right: 0;
  top: 4.5rem;
  bottom: 5.5rem;
  width: 450px;
  background: rgba(8, 14, 28, 0.96);
  border-left: 1px solid rgba(52, 211, 153, 0.15);
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 0.22s ease-out both;
  z-index: 30;
  pointer-events: auto;
`;

const PanelHeader = styled.div`
  padding: 0.75rem 1rem 0.6rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

const OutfallId = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #8896ac;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.6rem;
`;

const MetaChip = styled.span`
  font-size: 0.62rem;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  color: #8896ac;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 2px 6px;
  white-space: nowrap;
`;

const ComplianceBadge = styled.span<{ $status: ComplianceStatus }>`
  font-size: 0.62rem;
  font-weight: 700;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  border-radius: 999px;
  padding: 2px 8px;
  ${({ $status }) => {
    if ($status === 'green')
      return css`
        background: rgba(52, 211, 153, 0.15);
        color: #34d399;
      `;
    if ($status === 'amber')
      return css`
        background: rgba(251, 191, 36, 0.15);
        color: #fbbf24;
      `;
    return css`
      background: rgba(248, 113, 113, 0.15);
      color: #f87171;
    `;
  }}
`;

// ─── exceedance summary ────────────────────────────────────────────────────────

const ExceedanceBox = styled.div<{ $hasExceedance: boolean }>`
  margin: 0.5rem 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: ${({ $hasExceedance }) =>
    $hasExceedance ? 'rgba(248,113,113,0.08)' : 'rgba(52,211,153,0.06)'};
  border: 1px solid
    ${({ $hasExceedance }) => ($hasExceedance ? 'rgba(248,113,113,0.2)' : 'rgba(52,211,153,0.15)')};
  flex-shrink: 0;
`;

const ExceedanceTitle = styled.div`
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8896ac;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  margin-bottom: 0.2rem;
`;

const ExceedanceDetail = styled.div`
  font-size: 0.72rem;
  color: #cbd5e1;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  line-height: 1.4;
`;

// ─── parameter table ──────────────────────────────────────────────────────────

const TableScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 0 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
  padding: 0.6rem 1rem 0.3rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family:
    'DM Sans',
    -apple-system,
    sans-serif;
`;

const TH = styled.th`
  font-size: 0.58rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  padding: 0.3rem 0.5rem;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
`;

const TR = styled.tr<{ $exceedance?: boolean }>`
  background: ${({ $exceedance }) => ($exceedance ? 'rgba(248,113,113,0.05)' : 'transparent')};
  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const TD = styled.td`
  font-size: 0.68rem;
  color: #cbd5e1;
  padding: 0.3rem 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  vertical-align: middle;
`;

const ParamName = styled(TD)`
  color: #e2e8f0;
  font-weight: 500;
  max-width: 120px;
  white-space: normal;
  line-height: 1.3;
`;

const PctCell = styled(TD)<{ $pct: number | null }>`
  font-weight: 600;
  color: ${({ $pct }) => {
    if ($pct === null) return '#64748b';
    if ($pct > 100) return '#f87171';
    if ($pct >= 75) return '#fbbf24';
    return '#34d399';
  }};
`;

const TrendIcon = styled.span<{ $dir: TrendDirection }>`
  font-size: 0.75rem;
  color: ${({ $dir }) => ($dir === 'up' ? '#f87171' : $dir === 'down' ? '#34d399' : '#64748b')};
`;

const NoDataCell = styled(TD)`
  color: #64748b;
  font-style: italic;
`;

// ─── helpers ──────────────────────────────────────────────────────────────────

function trendIcon(dir: TrendDirection): string {
  return dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';
}

function complianceLabel(status: ComplianceStatus): string {
  return status === 'green'
    ? 'In Compliance'
    : status === 'amber'
      ? 'Minor Exceedances'
      : 'Significant Exceedances';
}

// Group parameter rows by parameter name for display
function groupByParam(rows: IParameterLimitRow[]): Map<string, IParameterLimitRow[]> {
  const map = new Map<string, IParameterLimitRow[]>();
  rows.forEach((r) => {
    const key = `${r.parameterCode}:${r.monitoringLocationDesc}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  });
  return map;
}

// ─── component ────────────────────────────────────────────────────────────────

const OutfallDetailPanel: React.FC<IOutfallDetailPanelProps> = ({ outfall, onClose }) => {
  const paramGroups = groupByParam(outfall.parameters);

  return (
    <Panel>
      <PanelHeader>
        <HeaderRow>
          <OutfallId>Outfall {outfall.id} · {outfall.facilityName}</OutfallId>
          <CloseBtn onClick={onClose} aria-label="Close panel">
            ✕
          </CloseBtn>
        </HeaderRow>
        <MetaRow>
          <ComplianceBadge $status={outfall.complianceStatus}>
            {complianceLabel(outfall.complianceStatus)}
          </ComplianceBadge>
          <MetaChip>{outfall.typeDesc}</MetaChip>
          <MetaChip>Receiving: {outfall.receivingWater}</MetaChip>
          <MetaChip>HUC-12: {outfall.huc12}</MetaChip>
        </MetaRow>
      </PanelHeader>

      <ExceedanceBox $hasExceedance={outfall.exceedanceCount12mo > 0}>
        <ExceedanceTitle>Exceedance Summary · last 12 months</ExceedanceTitle>
        {outfall.exceedanceCount12mo === 0 ? (
          <ExceedanceDetail>No exceedances recorded.</ExceedanceDetail>
        ) : (
          <ExceedanceDetail>
            {outfall.exceedanceCount12mo} exceedance{outfall.exceedanceCount12mo !== 1 ? 's' : ''}.
            {outfall.lastExceedanceParam && (
              <>
                {' '}
                Last: <strong>{outfall.lastExceedanceParam}</strong>
              </>
            )}
            {outfall.lastExceedanceDate && <> on {outfall.lastExceedanceDate}</>}
            {outfall.lastExceedancePct != null && <> ({outfall.lastExceedancePct}% over limit)</>}.
          </ExceedanceDetail>
        )}
      </ExceedanceBox>

      <TableScroll>
        <SectionLabel>Discharge Parameters</SectionLabel>
        <Table>
          <thead>
            <tr>
              <TH>Parameter</TH>
              <TH>Limit type</TH>
              <TH>Current</TH>
              <TH>Limit</TH>
              <TH>% of limit</TH>
              <TH>Trend</TH>
              <TH style={{ width: 60 }}>12 mo</TH>
            </tr>
          </thead>
          <tbody>
            {Array.from(paramGroups.values()).map((limitRows) =>
              limitRows.map((row, rowIdx) => {
                const isExceedance = row.exceedanceCount12mo > 0;
                const showParamName = rowIdx === 0;
                const rowSpan = limitRows.length;

                return (
                  <TR key={`${row.parameterCode}-${row.limitType}`} $exceedance={isExceedance}>
                    {showParamName ? (
                      <ParamName rowSpan={rowSpan}>
                        {row.parameterName}
                        <div style={{ fontSize: '0.58rem', color: '#64748b', marginTop: 1 }}>
                          {row.monitoringLocationDesc}
                        </div>
                      </ParamName>
                    ) : null}
                    <TD>
                      {row.limitType}
                      <br />
                      <span style={{ fontSize: '0.58rem', color: '#64748b' }}>
                        {row.limitTypeDesc}
                      </span>
                    </TD>
                    {row.isNoDischarge ? (
                      <NoDataCell colSpan={4}>No discharge reported</NoDataCell>
                    ) : row.currentValue == null ? (
                      <NoDataCell colSpan={4}>No data</NoDataCell>
                    ) : (
                      <>
                        <TD>
                          {row.currentValueQualifier !== '=' ? row.currentValueQualifier : ''}
                          {row.currentValue} {row.currentValueUnit}
                        </TD>
                        <TD>
                          {row.limitValue != null
                            ? `${row.limitQualifier} ${row.limitValue} ${row.limitUnit}`
                            : '—'}
                        </TD>
                        <PctCell $pct={row.percentOfLimit}>
                          {row.percentOfLimit != null ? `${row.percentOfLimit}%` : '—'}
                        </PctCell>
                        <TD>
                          <TrendIcon $dir={row.trend}>{trendIcon(row.trend)}</TrendIcon>
                        </TD>
                      </>
                    )}
                    <TD>
                      {row.sparklineValues.length >= 3 ? (
                        <Sparklines data={row.sparklineValues} width={56} height={20} margin={2}>
                          <SparklinesLine
                            color={
                              row.exceedanceCount12mo > 0
                                ? '#f87171'
                                : row.percentOfLimit != null && row.percentOfLimit >= 75
                                  ? '#fbbf24'
                                  : '#34d399'
                            }
                            style={{ strokeWidth: 1.5, fill: 'none' }}
                          />
                        </Sparklines>
                      ) : (
                        <span style={{ fontSize: '0.58rem', color: '#64748b' }}>—</span>
                      )}
                    </TD>
                  </TR>
                );
              })
            )}
          </tbody>
        </Table>
      </TableScroll>
    </Panel>
  );
};

export default OutfallDetailPanel;
